import { useEffect, useMemo, useState } from "react";
import { isPazireshPicUrl, withoutPazireshAvatarThumbParam } from "../utils/pazireshAvatarUrl";
import { loadPazireshPicUrl } from "../utils/pazireshAvatarLoader";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import classNames from "@/common/utils/classNames";
import { getAvatarColorForUser, getAvatarInitials } from "./UserAvatar";

/**
 * تصویر تمام‌عرض هیروی پروفایل — بدون پارامتر thumb برای کیفیت بالاتر.
 */
export default function ProfileHeroPhoto({
  remoteId,
  fullName = "",
  avatarUrl,
  className = "",
  alt = "",
}) {
  const online = useOnlineStatus();
  const [displaySrc, setDisplaySrc] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  const resolvedUrl = useMemo(() => {
    if (!online || !avatarUrl) return null;
    if (isPazireshPicUrl(avatarUrl)) {
      return withoutPazireshAvatarThumbParam(avatarUrl);
    }
    return avatarUrl;
  }, [avatarUrl, online]);

  useEffect(() => {
    setDisplaySrc(null);
    setLoaded(false);
    setFailed(false);
    if (!resolvedUrl) return undefined;

    let cancelled = false;

    if (isPazireshPicUrl(resolvedUrl)) {
      loadPazireshPicUrl(resolvedUrl, { skipThumb: true }).then((src) => {
        if (cancelled) return;
        if (src) {
          setDisplaySrc(src);
        } else {
          setFailed(true);
        }
      });
    } else {
      setDisplaySrc(resolvedUrl);
    }

    return () => {
      cancelled = true;
    };
  }, [resolvedUrl]);

  const bg = getAvatarColorForUser(remoteId);
  const initials = getAvatarInitials(fullName);
  const showFallback = !displaySrc || failed || !loaded;

  return (
    <div className={classNames("absolute inset-0", className)}>
      <div
        className="absolute inset-0 flex size-full items-center justify-center transition-opacity duration-[350ms]"
        style={{ backgroundColor: bg, opacity: showFallback ? 1 : 0 }}
        aria-hidden={!showFallback}
      >
        <span className="text-[1.9rem] font-bold text-white/90">{initials}</span>
      </div>
      {displaySrc && !failed && (
        <img
          src={displaySrc}
          alt={alt}
          className="absolute inset-0 size-full object-cover object-top transition-opacity duration-[350ms]"
          style={{ opacity: loaded ? 1 : 0 }}
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => {
            setFailed(true);
            setLoaded(false);
          }}
        />
      )}
    </div>
  );
}
